import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { useEffect, useRef } from 'react';

import { TMessageThread } from '@/types/message';

import ScrollX from '@/@mantis/components/ScrollX';
import { AlertTriangleIcon } from '@/assets/icons/AlertTriangleIcon';

import LoadingState from '../LoadingState';

import MessageThreadMessageItem from './MessageThreadMessageItem';

// Styled component for subject headers
const SubjectHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  marginBottom: theme.spacing(1),
  textAlign: 'center',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    height: '1px',
    backgroundColor: theme.palette.divider,
    zIndex: 1,
  },
}));

const SubjectText = styled(Typography)(({ theme }) => ({
  display: 'inline-block',
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(0.5, 2),
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  fontWeight: 500,
  position: 'relative',
  zIndex: 2,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(3),
}));

interface Props {
  messageThread: TMessageThread | null;
  isLoading?: boolean;
  isFailed?: boolean;
}

const MessageThreadMessageList: React.FC<Props> = ({
  messageThread,
  isLoading = false,
  isFailed = false,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messageThread]);

  if (isLoading) {
    return <LoadingState />;
  }

  if (isFailed) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ flex: '1 1 100%' }} gap={1}>
        <AlertTriangleIcon color="error" fontSize="large" />
        <Typography variant="body2">Failed to load messages.</Typography>
      </Stack>
    );
  }

  if (!messageThread) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ flex: '1 1 100%' }} gap={1}>
        <Typography variant="body2">Select a thread to view messages.</Typography>
      </Stack>
    );
  }

  if (messageThread.messages.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ flex: '1 1 100%' }} gap={1}>
        <Typography variant="body2">No messages found.</Typography>
      </Stack>
    );
  }

  // Function to determine if we should show a subject header
  const shouldShowSubjectHeader = (index: number): string | null => {
    const currentMessage = messageThread.messages[index];
    const previousMessage = index > 0 ? messageThread.messages[index - 1] : null;

    // Show subject if it exists and is different from the previous message's subject
    if (currentMessage.subject && currentMessage.subject.trim()) {
      if (!previousMessage || previousMessage.subject !== currentMessage.subject) {
        return currentMessage.subject;
      }
    }

    return null;
  };

  return (
    <ScrollX
      sx={{
        overflowX: 'auto',
        flex: '1 1 100%',
        display: 'flex',
      }}
    >
      <Stack direction="column" gap={2.5} sx={{ flex: '1 1 100%', px: 2, py: 4 }}>
        {messageThread.messages.map((message, index) => {
          const subjectToShow = shouldShowSubjectHeader(index);

          return (
            <Box key={message.id}>
              {subjectToShow && (
                <SubjectHeader>
                  <SubjectText variant="body2">{subjectToShow}</SubjectText>
                </SubjectHeader>
              )}
              <MessageThreadMessageItem message={message} messageSession={messageThread.session} />
            </Box>
          );
        })}

        <Box ref={bottomRef} />
      </Stack>
    </ScrollX>
  );
};

export default MessageThreadMessageList;
